import DeepInsights from "@/components/chat/DeepInsights.tsx";
import FLAGS from "@/constants/flags.ts";

import EventBus from "@/lib/eventBus";
import {
  createChat,
  getChat,
  getChatSuggestions,
  submitMessage,
} from "@/lib/serverComm";
import {
  useAuthenticationContext,
  useCurrentAccountId,
} from "@/store/authContext/authContext";
import { ChatData, Message } from "@/types/chat.ts";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChatView from "./ChatView";
import QueryBar from "./QueryBar";
import { PARITA_COMMUNITY_DOMAIN } from "@/constants/user";

const paritaLogoCopilot = new URL(
  "../../assets/peoplescape-assistant-1.png",
  import.meta.url,
).toString();

interface ChatProps {
  embedded?: boolean;
  embeddedContext?: string;
  insightId?: string;
}

const Chat: React.FC<ChatProps> = ({
  embedded,
  embeddedContext,
  insightId,
}) => {
  const { id } = useParams();
  const location = useLocation();

  const navigate = useNavigate();
  const [chatData, setChatData] = useState<ChatData>({
    messages: [],
    suggestions: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEmptyChat, setIsEmptyChat] = useState(true);

  const currentAccountId = useCurrentAccountId(),
    isParitaCommunity = currentAccountId === PARITA_COMMUNITY_DOMAIN;
  const chatIdRef = useRef<string | null>(null);
  const { state, dispatch } = useAuthenticationContext();
  const { userData: user, chatState: chats } = state;
  const pendingInitialChats = chats.pendingInitialChats;
  if (!user) {
    throw new Error("User data is not available");
  }
  const hasSubmittedRef = useRef(false);
  const emptyChatPrompts = [
    "Which job families have the longest time-to-fill?",
    "Can you show me any compensation gaps across my divisions?",
    "Provide the conversion rates for each stage of our recruitment funnel",
  ];

  const getTempMessages = (userQuery: string): Message[] => {
    return [
      { id: Date.now(), text: userQuery, sender: "user" },
      {
        id: Date.now() + 1,
        text: "",
        sender: "agent",
        status: "pending",
      },
    ];
  };

  useEffect(() => {
    chatIdRef.current = id || null;
  }, [id]);

  // Load initial report config and set correct dataset
  useEffect(() => {
    EventBus.$on("chat:resetChat", () => {
      setChatData({
        messages: [],
        suggestions: [],
      });
      setIsEmptyChat(true);
      setIsLoading(false);
      hasSubmittedRef.current = false;
      chatIdRef.current = null;
    });

    return () => {
      EventBus.$off("chat:resetChat");
    };
  }, [id, dispatch]);

  useEffect(() => {
    const fetchChat = async () => {
      if (id) {
        try {
          const response = await getChat(id);
          let messages = response.messages;
          // multiple chats might await for the response, use global storage to populate temporary pending messages

          const isLastPendingMessagePresent =
            response.messages.length > 0 &&
            response.messages[response.messages.length - 1].status ===
              "pending" &&
            response.messages[response.messages.length - 1].sender === "agent";
          if (pendingInitialChats[id] && !isLastPendingMessagePresent) {
            messages = messages.filter(
              (message: Message) => message.status == "pending",
            );
            messages.push(...getTempMessages(pendingInitialChats[id]));
          } else if (pendingInitialChats[id]) {
            dispatch({ type: "removePendingChatAction", payload: id });
          }

          setChatData({
            messages,
            suggestions: response.suggestions || [],
          });
          setIsEmptyChat(false);
        } catch (error) {
          console.error("Error fetching chat:", error);
        }
      } else {
        // For new chat, get initial suggestions
        try {
          setChatData({
            messages: [],
            suggestions: [],
          });
        } catch (error) {
          console.error("Error fetching suggestions:", error);
          setChatData({
            messages: [],
            suggestions: [],
          });
          setIsEmptyChat(true);
        }
      }
    };

    fetchChat();
  }, [id, dispatch, pendingInitialChats]);

  useEffect(() => {
    if (
      !hasSubmittedRef.current &&
      location.state &&
      location.state.initialQuery &&
      !isLoading
    ) {
      handleSubmit(location.state.initialQuery, location.state.type);
      hasSubmittedRef.current = true;
    }
  });

  const handleSubmit = async (userQuery: string, type?: string) => {
    // Embedded chats might have an additional optional embedded context, it should only be added once.
    if (isEmptyChat && embedded && embeddedContext) {
      userQuery = embeddedContext + userQuery;
    }

    setIsEmptyChat(false);
    setIsLoading(true);

    setChatData((prevData) => ({
      messages: [...(prevData?.messages || []), ...getTempMessages(userQuery)],
      suggestions: [],
    }));

    try {
      if (!chatIdRef.current) {
        const { chatId } = await createChat(type, insightId);
        chatIdRef.current = chatId;

        dispatch({
          type: "addChatAction",
          payload: {
            id: chatId,
            title: "New Chat",
            updated_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
            type: type,
          },
        });

        dispatch({
          type: "setPendingChatsAction",
          payload: { [chatId]: userQuery },
        });
        navigate(
          embedded && insightId !== undefined
            ? `/deep-insights/${insightId}/${chatId}`
            : `/chat/${chatId}`,
          { replace: true },
        );
      }

      if (!user.sidebar_settings.expandedGroups.includes("chats")) {
        dispatch({
          type: "updateUserSidebarSettingsAction",
          payload: {
            ...user.sidebar_settings,
            expandedGroups: [...user.sidebar_settings.expandedGroups, "chats"],
          },
        });
      }

      const result = await submitMessage(
        userQuery,
        chatIdRef.current!,
        currentAccountId,
        type,
      );
      dispatch({
        type: "removePendingChatAction",
        payload: chatIdRef.current!,
      });
      EventBus.$emit("sidebar:refreshChats");

      if (result.chatData.id === chatIdRef.current) {
        const newSuggestions = await getChatSuggestions(chatIdRef.current!);
        setChatData({
          ...result.chatData,
          suggestions: newSuggestions,
        });
      }
    } catch (error) {
      if (chatIdRef.current) {
        dispatch({
          type: "removePendingChatAction",
          payload: chatIdRef.current!,
        });
      }
      console.error("Error sending message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center h-full relative">
      <main className="flex-grow overflow-hidden">
        <div className="h-full">
          <ChatView
            messages={chatData.messages}
            suggestions={!isEmptyChat ? chatData.suggestions : []}
            onSuggestionSelect={handleSubmit}
            embedded={embedded}
          />
        </div>
      </main>

      <div
        className={`absolute left-0 right-0 transition-all duration-500 ease-in-out`}
        style={{
          top: isEmptyChat ? "" : "calc(100% - 96px)",
        }}
      >
        <div className="container mx-auto">
          {isEmptyChat && !embedded && isParitaCommunity && (
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Welcome to PeopleScape Community
              </h1>
              <p className="text-lg text-gray-600">
                Ask complex workforce questions. Get executive-ready answers.
              </p>
            </div>
          )}
          {!embedded && (
            <img
              src={paritaLogoCopilot}
              alt="PeopleScape Logo"
              className={`mx-auto ${
                isEmptyChat ? "w-[700px] mb-0" : "w-0"
              } h-auto transition-all duration-300 ease-in-out`}
            />
          )}
          <QueryBar
            id={chatIdRef.current || null}
            className="mx-auto"
            onSubmit={handleSubmit}
            isLoading={isLoading}
            suggestions={isEmptyChat ? chatData.suggestions : []}
            embedded={embedded}
          />
          {isEmptyChat && !embedded && isParitaCommunity && (
            <div className="mt-20 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
              {emptyChatPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => handleSubmit(prompt)}
                  className="bg-orange-gradient p-[2px] rounded-full shadow-orange-glow hover:shadow-orange-glow-hover transition-all duration-300"
                >
                  <span className="block w-full h-full rounded-full bg-white text-gray-700 text-sm px-6 py-3">
                    {prompt}
                  </span>
                </button>
              ))}
            </div>
          )}
          {FLAGS.DEEP_INSIGHTS &&
            !embedded &&
            (state.deepInsightsState.enabled ? (
              <DeepInsights />
            ) : (
              <p className="text-gray-500 text-center mt-8">
                Deep Insights is not enabled for your account. Contact your
                administrator to learn more.
              </p>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Chat;
